import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, IconButton, Box, Typography } from '@mui/material';
import { Close as CloseIcon, Undo as UndoIcon } from '@mui/icons-material';
import { useShortcutsContext } from '../context/ShortcutsContext';

const ACTION_LABELS = {
  navigate: 'Navigated',
  create: 'Created',
  edit: 'Editing',
  delete: 'Deleted',
  save: 'Saved',
  refresh: 'Refreshed',
  search: 'Searching',
  globalSearch: 'Global Search',
  commandPalette: 'Command Palette',
  selectAll: 'Selected All',
  toggleSelect: 'Toggle Selection',
  selectNext: 'Next',
  selectPrev: 'Previous',
  showHelp: 'Shortcuts Help',
  close: 'Closed',
};

const UNDOABLE_ACTIONS = ['delete', 'edit', 'create'];

const ShortcutToastProvider = ({ children }) => {
  const { lastAction } = useShortcutsContext();
  const [toastState, setToastState] = useState({
    open: false,
    action: null,
    path: null,
    canUndo: false,
  });
  const [undoCallback, setUndoCallback] = useState(null);

  // Show toast when action is executed
  useEffect(() => {
    if (lastAction && lastAction.timestamp) {
      const actionLabel = ACTION_LABELS[lastAction.action] || lastAction.action;
      const canUndo = UNDOABLE_ACTIONS.includes(lastAction.action);

      setToastState({
        open: true,
        action: actionLabel,
        path: lastAction.path,
        canUndo,
      });

      // Auto-hide after 2 seconds if not undoable
      if (!canUndo) {
        setTimeout(() => {
          setToastState(prev => ({ ...prev, open: false }));
        }, 2000);
      }
    }
  }, [lastAction]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastState(prev => ({ ...prev, open: false }));
  };

  const handleUndo = () => {
    if (undoCallback) {
      undoCallback();
    }
    setToastState(prev => ({ ...prev, open: false }));
  };

  const getMessage = () => {
    const { action, path } = toastState;

    if (action === 'Navigated' && path) {
      const pageName = path.split('/').pop() || 'page';
      return `Navigated to ${pageName.charAt(0).toUpperCase() + pageName.slice(1)}`;
    }

    return action;
  };

  return (
    <>
      {children}
      <Snackbar
        open={toastState.open}
        autoHideDuration={toastState.canUndo ? null : 2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          sx={{
            minWidth: 300,
            alignItems: 'center',
          }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {toastState.canUndo && undoCallback && (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={handleUndo}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                color="inherit"
                onClick={handleClose}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Typography variant="body2" fontWeight={500}>
            {getMessage()}
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

/**
 * Hook to show a toast notification for a shortcut action
 */
export const useShortcutToast = () => {
  const { lastAction } = useShortcutsContext();

  const showToast = useCallback((action, message, options = {}) => {
    const { canUndo = false, onUndo = null, duration = 2000 } = options;

    // This will be implemented by the ShortcutToastProvider
    // For now, we just use the context's lastAction
  }, [lastAction]);

  return { showToast };
};

export default ShortcutToastProvider;
